import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "@/lib/api";

const SubContext = createContext(null);

export function SubscriptionProvider({ children, user }) {
  const [sub, setSub] = useState(user?.subscription || null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const plansResp = Object.keys(plans).length ? { data: { plans } } : await api.get("/subscription/plans");
      if (plansResp.data?.plans) setPlans(plansResp.data.plans);
      if (user) {
        const me = await api.get("/subscription/me");
        setSub(me.data.subscription);
      }
    } catch (e) {
      console.error("Subscription refresh failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user, plans]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Sync when AuthContext's user object updates
  useEffect(() => {
    if (user?.subscription) setSub(user.subscription);
  }, [user?.subscription]);

  const canUse = (tierRequired) => {
    if (!sub) return false;
    if (!["trialing", "active"].includes(sub.status)) return false;
    // Both trial and paid users see the tools their tier allows.
    const paidTiers = ["basic", "pro", "pro_unlimited", "consulting"];
    if (tierRequired === "basic") return paidTiers.includes(sub.tier);
    if (tierRequired === "pro") return ["pro", "pro_unlimited", "consulting"].includes(sub.tier);
    if (tierRequired === "pro_unlimited") return sub.tier === "pro_unlimited";
    return paidTiers.includes(sub.tier);
  };

  const isPro = () => canUse("pro");

  const badgeForTier = (t) => {
    if (t === "basic") return "Creator";
    if (t === "pro") return "Pro";
    if (t === "pro_unlimited") return "Pro Unlimited";
    if (t === "consulting") return "Consulting";
    return "Free trial";
  };

  const startCheckout = async (planTier) => {
    const { data } = await api.post("/subscription/checkout", {
      plan: planTier,
      origin_url: window.location.origin,
    });
    return data.url;
  };

  const checkStatus = async (sessionId) => {
    const { data } = await api.get(`/subscription/checkout/status/${sessionId}`);
    if (data.subscription) setSub(data.subscription);
    return data;
  };

  return (
    <SubContext.Provider value={{ sub, setSub, plans, loading, refresh, canUse, isPro, badgeForTier, startCheckout, checkStatus }}>
      {children}
    </SubContext.Provider>
  );
}

export const useSubscription = () => useContext(SubContext);
